import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lock, Check, Sparkles, Calendar, Loader2, Star, Brain, Target, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Payment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, fetchUser } = useAuth();
  const { language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('diagnostic');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [acceptedCGV, setAcceptedCGV] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // Check if returning from Stripe
  const sessionId = searchParams.get('session_id');

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (e) { console.warn('Products fetch:', e.message); } finally {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error(language === 'fr' ? 'Vérification du paiement expirée' : 'Payment check timed out');
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success(language === 'fr' ? 'Paiement réussi !' : 'Payment successful!');
        await fetchUser();
        navigate('/dashboard');
        return;
      } else if (response.data.status === 'expired') {
        toast.error(language === 'fr' ? 'Session expirée' : 'Session expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (e) {
      console.warn('Payment poll:', e.message);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  }, [language, fetchUser, navigate]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId, pollPaymentStatus]);

  const handleCheckout = async () => {
    setProcessing(true);

    try {
      const response = await axios.post(`${API}/payment/create-checkout`, {
        product: selectedProduct,
        origin_url: window.location.origin
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de paiement' : 'Payment failed'));
      setProcessing(false);
    }
  };

  const handlePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/promo/validate`, { code: promoCode.trim() });
      toast.success(language === 'fr' ? 'Code promo appliqué ! Accès débloqué.' : 'Promo code applied! Access unlocked.');
      await fetchUser();
      navigate('/dashboard');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Code invalide' : 'Invalid code');
      toast.error(msg);
    } finally {
      setPromoLoading(false);
    }
  };

  const diagnosticProduct = products.find(p => p.id === 'diagnostic');
  const planProduct = products.find(p => p.id === 'plan_90_days');
  const selectedProductData = products.find(p => p.id === selectedProduct);

  if (loading || sessionId) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
        <p className="text-stone-600">
          {sessionId 
            ? (language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...')
            : (language === 'fr' ? 'Chargement...' : 'Loading...')}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-green-600" />
            <span className="text-sm text-stone-600">
              {language === 'fr' ? 'Paiement sécurisé' : 'Secure payment'}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-1">
            {language === 'fr' ? 'Tu y es presque.' : 'You\'re almost there.'}
          </h1>
          <p className="text-stone-500 text-sm">
            {language === 'fr' 
              ? 'Tu as fait le plus dur : te poser les bonnes questions. L\'IA va maintenant transformer tes réponses en une feuille de route claire pour ton avenir.'
              : 'You\'ve done the hard part: asking yourself the right questions. The AI will now transform your answers into a clear roadmap for your future.'}
          </p>
        </div>

        {/* What you unlock - only show for diagnostic */}
        {!diagnosticProduct?.purchased && selectedProduct === 'diagnostic' && (
          <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm mb-4">
            <h3 className="font-serif text-sm font-semibold text-stone-900 mb-3">
              {language === 'fr' ? 'Ce que tu vas découvrir :' : 'What you\'ll discover:'}
            </h3>
            <div className="space-y-2.5">
              {[
                { icon: Brain, text: language === 'fr' ? 'Ton profil de personnalité unique' : 'Your unique personality profile', color: '#FF6B6B' },
                { icon: Target, text: language === 'fr' ? '5 pistes de carrière faites pour toi' : '5 career paths made for you', color: '#4ECDC4' },
                { icon: Sparkles, text: language === 'fr' ? '3 projets concrets à lancer' : '3 concrete projects to start', color: '#FFE66D' },
                { icon: FileText, text: language === 'fr' ? 'Export PDF + coaching IA quotidien' : 'PDF export + daily AI coaching', color: '#1A535C' },
              ].map((item, i) => (
                <div key={`k-${i}`} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}12` }}>
                    <item.icon className="w-3.5 h-3.5" style={{ color: item.color }} />
                  </div>
                  <span className="text-sm text-stone-600">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What you unlock - Plan 90 days */}
        {selectedProduct === 'plan_90_days' && !planProduct?.purchased && (
          <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm mb-4">
            <h3 className="font-serif text-sm font-semibold text-stone-900 mb-3">
              {language === 'fr' ? 'Ce que tu vas recevoir :' : 'What you\'ll receive:'}
            </h3>
            <div className="space-y-2.5">
              {[
                { icon: Calendar, text: language === 'fr' ? '12 semaines structurées en 3 phases' : '12 weeks structured in 3 phases', color: '#4ECDC4' },
                { icon: Target, text: language === 'fr' ? 'Des tâches concrètes semaine par semaine' : 'Concrete week-by-week tasks', color: '#FF6B6B' },
                { icon: Star, text: language === 'fr' ? 'Coaching IA synchronisé à ta progression' : 'AI coaching synced to your progress', color: '#FFE66D' },
              ].map((item, i) => (
                <div key={`k-${i}`} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}12` }}>
                    <item.icon className="w-3.5 h-3.5" style={{ color: item.color }} />
                  </div>
                  <span className="text-sm text-stone-600">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products */}
        <div className="space-y-3 mb-6">
          {/* Diagnostic Product */}
          {diagnosticProduct && !diagnosticProduct.purchased && (
            <div
              onClick={() => setSelectedProduct('diagnostic')}
              className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                selectedProduct === 'diagnostic'
                  ? 'border-[#FF6B6B] bg-[#FF6B6B]/5'
                  : 'border-stone-200 bg-white hover:border-stone-300'
              }`}
              data-testid="product-diagnostic"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 text-sm">{diagnosticProduct.name}</h3>
                    <p className="text-xs text-stone-500">{diagnosticProduct.description}</p>
                  </div>
                </div>
                <span className="font-serif text-xl font-bold text-stone-900 flex-shrink-0 ml-2">
                  {diagnosticProduct.price.toFixed(2)}€
                </span>
              </div>
              {selectedProduct === 'diagnostic' && (
                <div className="mt-2 flex items-center gap-1 text-[#FF6B6B]">
                  <Check className="w-4 h-4" />
                  <span className="text-xs font-medium">{language === 'fr' ? 'Sélectionné' : 'Selected'}</span>
                </div>
              )}
            </div>
          )}

          {diagnosticProduct?.purchased && (
            <div className="p-4 rounded-2xl border-2 border-green-200 bg-green-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900 text-sm">{diagnosticProduct.name}</h3>
                  <p className="text-xs text-green-600">{language === 'fr' ? 'Déjà acheté' : 'Already purchased'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Plan 90 Days */}
          {planProduct && !planProduct.purchased && (
            <div
              onClick={() => setSelectedProduct('plan_90_days')}
              className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                selectedProduct === 'plan_90_days'
                  ? 'border-[#4ECDC4] bg-[#4ECDC4]/5'
                  : 'border-stone-200 bg-white hover:border-stone-300'
              }`}
              data-testid="product-plan90"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4ECDC4] to-[#1A535C] flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900 text-sm">{planProduct.name}</h3>
                    <p className="text-xs text-stone-500">{planProduct.description}</p>
                  </div>
                </div>
                <span className="font-serif text-xl font-bold text-stone-900 flex-shrink-0 ml-2">
                  {planProduct.price.toFixed(2)}€
                </span>
              </div>
              {selectedProduct === 'plan_90_days' && (
                <div className="mt-2 flex items-center gap-1 text-[#4ECDC4]">
                  <Check className="w-4 h-4" />
                  <span className="text-xs font-medium">{language === 'fr' ? 'Sélectionné' : 'Selected'}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Promo Code */}
        <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm mb-3">
          <h3 className="text-sm font-medium text-stone-700 mb-3">
            {language === 'fr' ? 'Tu as un code promo ?' : 'Have a promo code?'}
          </h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder={language === 'fr' ? 'Entrer le code' : 'Enter code'}
              className="flex-1 px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B6B]/30 focus:border-[#FF6B6B] uppercase tracking-wider"
              data-testid="promo-code-input"
            />
            <Button
              onClick={handlePromoCode}
              disabled={!promoCode.trim() || promoLoading}
              className="bg-[#4ECDC4] hover:bg-[#3DBDB5] text-white rounded-xl px-4 text-sm"
              data-testid="promo-code-submit"
            >
              {promoLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                language === 'fr' ? 'Appliquer' : 'Apply'
              )}
            </Button>
          </div>
        </div>

        {/* CGV Consent */}
        <div className="bg-white rounded-3xl p-5 shadow-soft border border-stone-100 mb-3">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="cgv-consent-label">
            <input
              type="checkbox"
              checked={acceptedCGV}
              onChange={(e) => setAcceptedCGV(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-stone-300 text-[#FF6B6B] focus:ring-[#FF6B6B] accent-[#FF6B6B] flex-shrink-0"
              data-testid="cgv-consent-checkbox"
            />
            <span className="text-sm text-stone-600 leading-relaxed">
              {language === 'fr' ? (
                <>
                  J'accepte les{' '}
                  <Link to="/cgv" target="_blank" className="text-[#FF6B6B] underline hover:text-[#EE5253]" data-testid="cgv-link-payment">
                    Conditions Générales de Vente
                  </Link>{' '}
                  et je renonce expressément à mon droit de rétractation conformément à l'article L.221-28 du Code de la consommation.
                </>
              ) : (
                <>
                  I accept the{' '}
                  <Link to="/cgv" target="_blank" className="text-[#FF6B6B] underline hover:text-[#EE5253]" data-testid="cgv-link-payment">
                    Terms and Conditions of Sale
                  </Link>{' '}
                  and I expressly waive my right of withdrawal in accordance with Article L.221-28 of the French Consumer Code.
                </>
              )}
            </span>
          </label>
        </div>

        {/* Checkout Button */}
        <div className="bg-white rounded-3xl p-5 shadow-soft border border-stone-100">
          <div className="flex justify-between items-center mb-4">
            <span className="text-stone-600">Total</span>
            <span className="font-serif text-2xl font-bold text-stone-900">
              {selectedProductData?.price.toFixed(2)}€
            </span>
          </div>

          <Button
            onClick={handleCheckout}
            className="w-full bg-[#FF6B6B] hover:bg-[#EE5253] text-white rounded-xl h-12 font-medium flex items-center justify-center gap-2"
            disabled={processing || !selectedProductData || selectedProductData.purchased || !acceptedCGV}
            data-testid="checkout-button"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {language === 'fr' ? 'Redirection...' : 'Redirecting...'}
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                {language === 'fr' ? 'Payer avec Stripe' : 'Pay with Stripe'}
              </>
            )}
          </Button>

          {/* Security badges */}
          <div className="flex items-center justify-center gap-4 mt-4 text-stone-400">
            <div className="flex items-center gap-1">
              <Lock className="w-3 h-3" />
              <span className="text-xs">SSL</span>
            </div>
            <span className="text-xs">Powered by Stripe</span>
          </div>
        </div>

        {/* Reassurance */}
        <p className="text-center text-stone-400 text-xs mt-4 px-4 leading-relaxed">
          {language === 'fr' 
            ? 'Paiement unique et sécurisé. Pas d\'abonnement. Tes résultats sont disponibles à vie.'
            : 'One-time secure payment. No subscription. Your results are available for life.'}
        </p>
      </main>
    </div>
  );
};

// Success page component
export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUser } = useAuth();
  const { language } = useLanguage();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  const checkPayment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await fetchUser();
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setStatus('pending');
      }
    } catch (e) {
      console.warn('Payment status:', e.message);
      setStatus('error');
    }
  }, [sessionId, fetchUser, navigate]);

  useEffect(() => {
    if (sessionId) {
      checkPayment();
    }
  }, [sessionId, checkPayment]);

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
      <div className="text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-[#FF6B6B] mx-auto mb-4" />
            <p className="text-stone-600">{language === 'fr' ? 'Vérification...' : 'Verifying...'}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
              {language === 'fr' ? 'Paiement réussi !' : 'Payment successful!'}
            </h1>
            <p className="text-stone-600">{language === 'fr' ? 'Redirection...' : 'Redirecting...'}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="text-red-600 mb-4">{language === 'fr' ? 'Erreur de vérification' : 'Verification error'}</p>
            <Button onClick={() => navigate('/dashboard')}>
              {language === 'fr' ? 'Retour' : 'Back'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default Payment;
